import React, { useState } from 'react';
import { NewSearch } from '../Components/PIM/common/NewSearch';

/**
 * Hard-coded roster of bombiix-icon glyphs (mirrors assets/css/bombiix-icons.css).
 * `paths: 2` marks multi-path glyphs that need `.path1` / `.path2` inner spans.
 */
const BOMBIIX_ICONS = [
  { name: 'duration-counter' },
  { name: 'product-group' },
  { name: 'progress-indicator' },
  { name: 'calender' },
  { name: 'save' },
  { name: 'delete' },
  { name: 'rearrange' },
  { name: 'sort' },
  { name: 'publish' },
  { name: 'close' },
  { name: 'chevron-down' },
  { name: 'chevron-up' },
  { name: 'tag' },
  { name: 'search' },
  { name: 'setting' },
  { name: 'link' },
  { name: 'archive' },
  { name: 'approve' },
  { name: 'filter' },
  { name: 'pin' },
  { name: 'collapse', paths: 2 },
  { name: 'expand', paths: 2 },
  { name: 'card-view' },
  { name: 'table-view' },
  { name: 'colour-coading' },
  { name: 'validation' },
  { name: 'download' },
  { name: 'information' },
  { name: 'tooltip' },
  { name: 'duplicate' },
  { name: 'add' },
  { name: 'preview' },
  { name: 'upload' },
  { name: 'edit' },
  { name: 'inactive' },
  { name: 'active' },
];

/**
 * Copy a string to the clipboard. Prefers the async Clipboard API; falls back
 * to the deprecated execCommand path for insecure contexts or older browsers.
 */
async function copyToClipboard(text) {
  if (navigator?.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      console.error(error);
    }
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch (_) {
    return false;
  }
}

/**
 * Renders a bombiix icon glyph, handling both single-path and multi-path
 * (collapse / expand) icons.
 */
function IconGlyph({ name, paths, size = 32 }) {
  const cls = `bombiix-icon-${name}`;
  if (paths === 2) {
    return (
      <span className={cls} style={{ fontSize: size, lineHeight: 1 }}>
        <span className="path1" />
        <span className="path2" />
      </span>
    );
  }
  return <i className={cls} style={{ fontSize: size, lineHeight: 1 }} />;
}

/**
 * Bombiix icon library — dev catalogue at /bombiix-icons.
 * Renders every glyph in a searchable grid; click a tile to copy its CSS
 * class name (`bombiix-icon-xxx`) to the clipboard.
 */
function BombiixIcons() {
  const [query, setQuery] = useState('');
  const [copiedName, setCopiedName] = useState(null);

  const normalisedQuery = query.trim().toLowerCase();
  const filtered = normalisedQuery
    ? BOMBIIX_ICONS.filter((icon) => icon.name.toLowerCase().includes(normalisedQuery))
    : BOMBIIX_ICONS;

  const handleCopy = async (name) => {
    const snippet = `<i className="bombiix-icon-${name}" />`;
    const ok = await copyToClipboard(snippet);
    if (ok) {
      setCopiedName(name);
      setTimeout(() => setCopiedName((current) => (current === name ? null : current)), 1200);
    }
  };

  return (
    <div className="container mt-4 pb-5">
      <h2 className="mb-1">Bombiix Icons</h2>
      <p className="text-muted mb-3">Click any tile to copy the class name to your clipboard.</p>

      <div style={{ maxWidth: 320, marginBottom: 24 }}>
        <NewSearch
          className="bombiix-search"
          value={query}
          onChange={setQuery}
          placeholder="Search icons"
          testId="bombiix-icons-search"
        />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
          gap: 12,
        }}
      >
        {filtered.map((icon) => {
          const isCopied = copiedName === icon.name;
          const fullClass = `bombiix-icon-${icon.name}`;
          const snippet = `<i className="${fullClass}" />`;
          return (
            <div
              key={icon.name}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
                padding: '16px 8px 8px',
                background: '#ffffff',
                border: `1px solid ${isCopied ? '#73c5af' : '#d8d8d8'}`,
                borderRadius: 4,
                transition: 'border-color 0.15s ease',
              }}
            >
              <span style={{ color: '#3c3c3c' }}>
                <IconGlyph name={icon.name} paths={icon.paths} size={32} />
              </span>
              <code
                style={{
                  fontSize: 11,
                  color: '#525252',
                  wordBreak: 'break-all',
                  textAlign: 'center',
                  userSelect: 'all',
                  padding: '4px 6px',
                  background: '#f5f5f5',
                  borderRadius: 3,
                  width: '100%',
                }}
              >
                {snippet}
              </code>
              <button
                type="button"
                onClick={() => handleCopy(icon.name)}
                title={`Copy ${snippet}`}
                aria-label={`Copy JSX for ${fullClass}`}
                className={`bombiix-icon-container ${isCopied ? 'bombiix-icon-container--green' : 'bombiix-icon-container--blue'}`}
              >
                <i className={isCopied ? 'bombiix-icon-approve' : 'bombiix-icon-duplicate'} />
              </button>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <p className="text-muted mt-4">No icons match &quot;{query}&quot;.</p>
      )}
    </div>
  );
}

export default BombiixIcons;
